import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";

import CancelIcon from "@material-ui/icons/Cancel";
import VisibilityIcon from "@material-ui/icons/Visibility";
import SendIcon from "@material-ui/icons/Send";
import ReceiptIcon from "@material-ui/icons/Receipt";

import MainContainer from "../../components/MainContainer";
import { PageHeader, Section, StatusBadge } from "../../components/ui";
import EmptyState from "../../components/EmptyState";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import CampaignDetailsDialog from "./CampaignDetailsDialog";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { logError } from "../../utils/logger";
import { getTokens, SEMANTIC } from "../../styles/tokens";

const TEMPLATE_VARIABLES = [
  "{nome}",
  "{vencimento}",
  "{valor}",
  "{linhaDigitavel}",
  "{pix}",
  "{link}",
  "{contrato}",
];

// Statuses considered "done" — polling stops when no campaign is in progress
const TERMINAL_STATUSES = ["finished", "cancelled", "failed"];

const POLL_INTERVAL_MS = 10000;

// Backend (IspInvoiceCampaignController) accepts daysAhead between 0 and 90
const MAX_DAYS_AHEAD = 90;

// Only WhatsApp connections (Baileys or official) can send invoice campaigns;
// facebook/instagram connections are also returned by GET /whatsapp.
const isWhatsappConnection = (w) =>
  !w?.channel || String(w.channel).toLowerCase().includes("whatsapp");

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    stack: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
      paddingBottom: 8,
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 16,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    full: { gridColumn: "1 / -1" },
    field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary },
    help: { fontSize: 12, color: t.textMuted },
    placeholder: { color: t.textMuted },
    variablesBox: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 6,
    },
    variableChip: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: 11.5,
      fontWeight: 500,
      color: t.textSecondary,
      backgroundColor: "transparent",
      "&:hover, &:focus": { backgroundColor: t.tint.soft + " !important", color: t.text },
    },
    actionsRow: {
      display: "flex",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 20,
      paddingTop: 16,
      borderTop: "1px solid " + t.line,
    },
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      color: theme.palette.primary.main,
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
    tableWrap: {
      overflowX: "auto",
      ...theme.scrollbarStyles,
      "& .MuiTable-root": { minWidth: 860 },
    },
    nameCell: { fontWeight: 600, color: t.text },
    nowrap: { whiteSpace: "nowrap" },
    previewTotals: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: t.text,
      margin: "16px 0 0",
      paddingTop: 16,
      borderTop: "1px solid " + t.line,
    },
    sample: {
      padding: 12,
      marginTop: 8,
      fontSize: 13,
      lineHeight: 1.5,
      color: t.text,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      borderRadius: t.radius.sm,
      border: "1px solid " + t.line,
      backgroundColor: t.muted,
    },
    sampleHeader: {
      fontWeight: 600,
      marginBottom: 4,
    },
    helperText: {
      fontSize: 13,
      color: t.textSecondary,
    },
    sampleNumber: {
      fontWeight: 400,
      color: t.textSecondary,
    },
  };
});

const isInProgress = (campaign) =>
  !TERMINAL_STATUSES.includes(String(campaign?.status || ""));

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Backend stores totals as totalItems/totalSent/totalFailed/totalSkipped on the campaign;
// pending is derived. Also tolerates a nested `totals` object.
const getTotals = (campaign) => {
  const src = campaign?.totals || campaign || {};
  const capitalize = (k) => k.charAt(0).toUpperCase() + k.slice(1);
  const pick = (key) => {
    if (src[`total${capitalize(key)}`] !== undefined) return toNumber(src[`total${capitalize(key)}`]);
    if (src[key] !== undefined && typeof src[key] !== "object") return toNumber(src[key]);
    return 0;
  };
  const sent = pick("sent");
  const failed = pick("failed");
  const skipped = pick("skipped");
  const items = pick("items");
  let pending = 0;
  if (src.pending !== undefined) pending = toNumber(src.pending);
  else if (src.totalPending !== undefined) pending = toNumber(src.totalPending);
  else pending = Math.max(items - sent - failed - skipped, 0);
  return { pending, sent, failed, skipped };
};

const InvoiceCampaigns = () => {
  const classes = useStyles();
  const [details, setDetails] = useState(null);
  const { datetimeToClient } = useDate();

  const initialForm = {
    name: "",
    whatsappId: "",
    erpIntegrationId: "",
    filter: "overdue",
    daysAhead: 5,
    template: i18n.t("invoiceCampaigns.form.defaultTemplate"),
    scheduledAt: "",
    metaTemplateName: "",
  };

  const [form, setForm] = useState(initialForm);
  const [whatsapps, setWhatsapps] = useState([]);
  const [erpIntegrations, setErpIntegrations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const templateRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const selectedWhatsapp = whatsapps.find(
    (w) => String(w.id) === String(form.whatsappId)
  );
  const isOfficialChannel =
    !!selectedWhatsapp &&
    String(selectedWhatsapp.channel || "").toLowerCase().includes("oficial");

  const fetchCampaigns = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/isp/invoice-campaigns");
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.records)
        ? data.records
        : Array.isArray(data?.campaigns)
        ? data.campaigns
        : [];
      if (isMountedRef.current) setCampaigns(list);
    } catch (err) {
      if (!silent) toastError(err);
      else logError("Falha ao atualizar campanhas de fatura", err);
    } finally {
      if (!silent && isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp");
        if (active) {
          setWhatsapps((Array.isArray(data) ? data : []).filter(isWhatsappConnection));
        }
      } catch (err) {
        toastError(err);
      }
      try {
        // GET /isp/erp-integrations returns every SGP/IXC integration of the
        // company (GET /queueIntegration is paginated to 20 rows).
        const { data } = await api.get("/isp/erp-integrations");
        if (active) setErpIntegrations(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();
    fetchCampaigns();
    return () => {
      active = false;
    };
  }, [fetchCampaigns]);

  // Poll while any campaign is in progress
  const hasInProgress = campaigns.some(isInProgress);
  useEffect(() => {
    if (!hasInProgress) return undefined;
    const timer = setInterval(() => fetchCampaigns(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasInProgress, fetchCampaigns]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const insertVariable = (variable) => {
    setForm((prev) => {
      const current = prev.template || "";
      const el = templateRef.current;
      if (el && typeof el.selectionStart === "number") {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        return {
          ...prev,
          template: current.slice(0, start) + variable + current.slice(end),
        };
      }
      return { ...prev, template: `${current}${current ? " " : ""}${variable}` };
    });
  };

  const validateForm = () => {
    const errors = [];
    if (!form.name || form.name.trim().length < 2) {
      errors.push(i18n.t("invoiceCampaigns.validation.nameRequired"));
    }
    if (!form.whatsappId) errors.push(i18n.t("invoiceCampaigns.validation.whatsappRequired"));
    if (!form.erpIntegrationId) errors.push(i18n.t("invoiceCampaigns.validation.erpRequired"));
    if (!form.template || !form.template.trim()) {
      errors.push(i18n.t("invoiceCampaigns.validation.templateRequired"));
    }
    if (form.filter === "upcoming") {
      const n = Number(form.daysAhead);
      if (!Number.isInteger(n) || n < 0 || n > MAX_DAYS_AHEAD) {
        errors.push(i18n.t("invoiceCampaigns.validation.daysAheadInvalid"));
      }
    }
    if (isOfficialChannel && !form.metaTemplateName) {
      errors.push(i18n.t("invoiceCampaigns.validation.metaTemplateRequired"));
    }
    return errors;
  };

  const buildBody = () => {
    const body = {
      name: form.name.trim(),
      whatsappId: Number(form.whatsappId),
      erpIntegrationId: Number(form.erpIntegrationId),
      filter: form.filter,
      daysAhead: form.filter === "upcoming" ? Number(form.daysAhead) : undefined,
      template: form.template,
      scheduledAt: form.scheduledAt
        ? new Date(form.scheduledAt).toISOString()
        : null,
    };
    if (form.metaTemplateName) body.metaTemplateName = form.metaTemplateName.trim();
    return body;
  };

  const handlePreview = async () => {
    const errors = validateForm();
    if (errors.length) {
      errors.forEach((e) => toast.error(e));
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/isp/invoice-campaigns/preview", buildBody());
      if (!isMountedRef.current) return;
      setPreview(data || {});
      setPreviewOpen(true);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setPreviewing(false);
    }
  };

  const handleCreate = async () => {
    const errors = validateForm();
    if (errors.length) {
      errors.forEach((e) => toast.error(e));
      return;
    }
    setCreating(true);
    try {
      await api.post("/isp/invoice-campaigns", buildBody());
      toast.success(i18n.t("invoiceCampaigns.toasts.created"));
      if (!isMountedRef.current) return;
      setPreviewOpen(false);
      setPreview(null);
      setForm((prev) => ({ ...initialForm, whatsappId: prev.whatsappId, erpIntegrationId: prev.erpIntegrationId }));
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setCreating(false);
    }
  };

  const handleCancelCampaign = async () => {
    if (!cancelling) return;
    try {
      await api.post(`/isp/invoice-campaigns/${cancelling.id}/cancel`);
      toast.success(i18n.t("invoiceCampaigns.toasts.cancelled"));
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    } finally {
      setCancelling(null);
      setConfirmCancelOpen(false);
    }
  };

  const formatStatus = (status) => {
    const key = `invoiceCampaigns.status.${status}`;
    const translated = i18n.t(key);
    return translated === key ? status || "-" : translated;
  };

  // `filter` da campanha: overdue | upcoming | alert (aviso de falha, criado pelo Problema Geral)
  const formatFilter = (filter) => {
    const f = String(filter || "");
    if (!f) return "-";
    const key = `invoiceCampaigns.filters.${f}`;
    const translated = i18n.t(key);
    if (translated !== key) return translated;
    const legacy = {
      overdue: "invoiceCampaigns.form.filterOverdue",
      upcoming: "invoiceCampaigns.form.filterUpcoming",
    }[f];
    return legacy ? i18n.t(legacy) : f;
  };

  const getStatusTone = (status) => {
    const s = String(status || "").toLowerCase();
    if (["running", "processing", "sending", "em_andamento"].includes(s)) return "warning";
    if (["finished", "completed", "done", "finalizada"].includes(s)) return "success";
    if (["scheduled"].includes(s)) return "info";
    if (["failed", "cancelled", "canceled"].includes(s)) return "error";
    return "neutral";
  };

  // rótulo do item escolhido nos selects (o campo usa `displayEmpty` com "Selecione…")
  const whatsappLabel = (id) => {
    const w = whatsapps.find((it) => String(it.id) === String(id));
    return w ? `${w.name}${w.channel ? ` (${w.channel})` : ""}` : id;
  };
  const erpLabel = (id) => {
    const it = erpIntegrations.find((e) => String(e.id) === String(id));
    return it ? `${it.name} (${it.type})` : id;
  };
  const selectPlaceholder = <span className={classes.placeholder}>Selecione…</span>;

  const skipped = preview?.skipped || {};
  const samples = Array.isArray(preview?.samples) ? preview.samples : [];
  // Backend `total` counts every collected invoice (including skipped ones);
  // what will actually be sent is total minus all skipped buckets.
  const totalSkipped =
    toNumber(skipped.optout) +
    toNumber(skipped.dedup) +
    toNumber(skipped.needsTemplate) +
    toNumber(skipped.noPhone);
  const sendableTotal = Math.max(toNumber(preview?.total) - totalSkipped, 0);

  return (
    <MainContainer maxWidth scroll>
      <ConfirmationModal
        title={
          cancelling &&
          `${i18n.t("invoiceCampaigns.confirmationModal.cancelTitle")} ${cancelling.name}?`
        }
        open={confirmCancelOpen}
        onClose={() => {
          setConfirmCancelOpen(false);
          setCancelling(null);
        }}
        onConfirm={handleCancelCampaign}
      >
        {i18n.t("invoiceCampaigns.confirmationModal.cancelMessage")}
      </ConfirmationModal>

      <CampaignDetailsDialog
        campaign={details?.campaign || null}
        initialFilter={details?.filter}
        onClose={() => setDetails(null)}
      />

      <Dialog
        open={previewOpen}
        onClose={() => {
          if (!creating) setPreviewOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="invoice-campaign-preview-title"
      >
        <DialogTitle id="invoice-campaign-preview-title">
          {i18n.t("invoiceCampaigns.preview.title")}
        </DialogTitle>
        <DialogContent>
          <div className={classes.previewTotals}>
            <StatusBadge
              color="brand"
              size="lg"
              dot={false}
              label={`${i18n.t("invoiceCampaigns.preview.total")}: ${sendableTotal}`}
            />
            <StatusBadge
              color="neutral"
              size="lg"
              dot={false}
              label={`${i18n.t("invoiceCampaigns.preview.optout")}: ${skipped.optout ?? 0}`}
            />
            <StatusBadge
              color="neutral"
              size="lg"
              dot={false}
              label={`${i18n.t("invoiceCampaigns.preview.dedup")}: ${skipped.dedup ?? 0}`}
            />
            <StatusBadge
              color="neutral"
              size="lg"
              dot={false}
              label={`${i18n.t("invoiceCampaigns.preview.needsTemplate")}: ${skipped.needsTemplate ?? 0}`}
            />
          </div>
          <div className={classes.sectionTitle}>
            {i18n.t("invoiceCampaigns.preview.samples")}
          </div>
          {samples.length === 0 && (
            <div className={classes.helperText} style={{ marginTop: 8 }}>
              {i18n.t("invoiceCampaigns.preview.noSamples")}
            </div>
          )}
          {samples.map((sample, idx) => (
            <div key={idx} className={classes.sample}>
              <div className={classes.sampleHeader}>
                {sample.contactName || "-"}{" "}
                <span className={classes.sampleNumber}>
                  {sample.contactNumber ? `(${sample.contactNumber})` : ""}
                </span>
              </div>
              <div>{sample.renderedMessage}</div>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPreviewOpen(false)}
            disabled={creating}
          >
            {i18n.t("invoiceCampaigns.buttons.close")}
          </Button>
          <Button
            onClick={handleCreate}
            color="primary"
            variant="contained"
            disabled={creating || sendableTotal <= 0}
            className={classes.btnWrapper}
          >
            {i18n.t("invoiceCampaigns.buttons.createAndSend")}
            {creating && <CircularProgress size={24} className={classes.buttonProgress} />}
          </Button>
        </DialogActions>
      </Dialog>

      <PageHeader
        title={i18n.t("invoiceCampaigns.title")}
        subtitle="Envie as faturas do ERP em massa pelo WhatsApp."
        badge={<StatusBadge color="neutral" dot={false} label={String(campaigns.length)} />}
      />

      <div className={classes.stack}>
        <Section title="Nova campanha">
          <div className={classes.formGrid}>
            <div className={classes.field}>
              <label className={classes.label} htmlFor="invoice-campaign-name">
                {i18n.t("invoiceCampaigns.form.name")} *
              </label>
              <TextField
                id="invoice-campaign-name"
                value={form.name}
                onChange={handleChange("name")}
                variant="outlined"
                size="small"
                fullWidth
                required
              />
            </div>
            <div className={classes.field}>
              <label className={classes.label} id="invoice-campaign-whatsapp-label">
                {i18n.t("invoiceCampaigns.form.whatsapp")} *
              </label>
              <FormControl variant="outlined" size="small" fullWidth required>
                <Select
                  labelId="invoice-campaign-whatsapp-label"
                  value={form.whatsappId}
                  onChange={handleChange("whatsappId")}
                  displayEmpty
                  renderValue={(value) => (value === "" ? selectPlaceholder : whatsappLabel(value))}
                >
                  <MenuItem value="">{i18n.t("invoiceCampaigns.form.none")}</MenuItem>
                  {whatsapps.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name}
                      {w.channel ? ` (${w.channel})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className={classes.field}>
              <label className={classes.label} id="invoice-campaign-erp-label">
                {i18n.t("invoiceCampaigns.form.erpIntegration")} *
              </label>
              <FormControl variant="outlined" size="small" fullWidth required>
                <Select
                  labelId="invoice-campaign-erp-label"
                  value={form.erpIntegrationId}
                  onChange={handleChange("erpIntegrationId")}
                  displayEmpty
                  renderValue={(value) => (value === "" ? selectPlaceholder : erpLabel(value))}
                >
                  <MenuItem value="">{i18n.t("invoiceCampaigns.form.none")}</MenuItem>
                  {erpIntegrations.map((it) => (
                    <MenuItem key={it.id} value={it.id}>
                      {it.name} ({it.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className={classes.field}>
              <label className={classes.label} id="invoice-campaign-filter-label">
                {i18n.t("invoiceCampaigns.form.filter")}
              </label>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  labelId="invoice-campaign-filter-label"
                  value={form.filter}
                  onChange={handleChange("filter")}
                >
                  <MenuItem value="overdue">
                    {i18n.t("invoiceCampaigns.form.filterOverdue")}
                  </MenuItem>
                  <MenuItem value="upcoming">
                    {i18n.t("invoiceCampaigns.form.filterUpcoming")}
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
            {form.filter === "upcoming" && (
              <div className={classes.field}>
                <label className={classes.label} htmlFor="invoice-campaign-days-ahead">
                  {i18n.t("invoiceCampaigns.form.daysAhead")}
                </label>
                <TextField
                  id="invoice-campaign-days-ahead"
                  type="number"
                  inputProps={{ min: 0, max: MAX_DAYS_AHEAD, step: 1 }}
                  value={form.daysAhead}
                  onChange={handleChange("daysAhead")}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>
            )}
            <div className={classes.field}>
              <label className={classes.label} htmlFor="invoice-campaign-scheduled-at">
                {i18n.t("invoiceCampaigns.form.scheduledAt")}
              </label>
              <TextField
                id="invoice-campaign-scheduled-at"
                type="datetime-local"
                value={form.scheduledAt}
                onChange={handleChange("scheduledAt")}
                variant="outlined"
                size="small"
                fullWidth
              />
              <span className={classes.help}>{i18n.t("invoiceCampaigns.form.scheduledAtHelp")}</span>
            </div>
            {isOfficialChannel && (
              <div className={classes.field}>
                <label className={classes.label} htmlFor="invoice-campaign-meta-template">
                  {i18n.t("invoiceCampaigns.form.metaTemplateName")} *
                </label>
                <TextField
                  id="invoice-campaign-meta-template"
                  value={form.metaTemplateName}
                  onChange={handleChange("metaTemplateName")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                />
                <span className={classes.help}>{i18n.t("invoiceCampaigns.form.metaTemplateNameHelp")}</span>
              </div>
            )}

            <div className={`${classes.field} ${classes.full}`}>
              <label className={classes.label} htmlFor="invoice-campaign-template">
                {i18n.t("invoiceCampaigns.form.template")} *
              </label>
              <TextField
                id="invoice-campaign-template"
                value={form.template}
                onChange={handleChange("template")}
                inputRef={templateRef}
                multiline
                minRows={4}
                maxRows={10}
                variant="outlined"
                fullWidth
                required
              />
              <div className={classes.variablesBox}>
                <span className={classes.help}>{i18n.t("invoiceCampaigns.form.variablesHelp")}</span>
                {TEMPLATE_VARIABLES.map((v) => (
                  <Chip
                    key={v}
                    size="small"
                    label={v}
                    variant="outlined"
                    className={classes.variableChip}
                    onClick={() => insertVariable(v)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className={classes.actionsRow}>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={handlePreview}
              disabled={previewing || creating}
              className={classes.btnWrapper}
            >
              {i18n.t("invoiceCampaigns.buttons.preview")}
              {previewing && <CircularProgress size={24} className={classes.buttonProgress} />}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleCreate}
              disabled={previewing || creating}
              className={classes.btnWrapper}
            >
              {i18n.t("invoiceCampaigns.buttons.createAndSend")}
              {creating && <CircularProgress size={24} className={classes.buttonProgress} />}
            </Button>
          </div>
        </Section>

        <Section title="Campanhas criadas" flush>
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("invoiceCampaigns.table.name")}</TableCell>
                  <TableCell>{i18n.t("invoiceCampaigns.table.type")}</TableCell>
                  <TableCell>{i18n.t("invoiceCampaigns.table.status")}</TableCell>
                  <TableCell align="right">{i18n.t("invoiceCampaigns.table.pending")}</TableCell>
                  <TableCell align="right">{i18n.t("invoiceCampaigns.table.sent")}</TableCell>
                  <TableCell align="right">{i18n.t("invoiceCampaigns.table.failed")}</TableCell>
                  <TableCell align="right">{i18n.t("invoiceCampaigns.table.skipped")}</TableCell>
                  <TableCell>{i18n.t("invoiceCampaigns.table.createdAt")}</TableCell>
                  <TableCell align="right">{i18n.t("invoiceCampaigns.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell className={classes.nameCell}>{campaign.name}</TableCell>
                      <TableCell>
                        <StatusBadge
                          color={String(campaign.filter) === "alert" ? "warning" : "neutral"}
                          dot={false}
                          label={formatFilter(campaign.filter)}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          color={getStatusTone(campaign.status)}
                          label={formatStatus(campaign.status)}
                        />
                      </TableCell>
                      <TableCell align="right">{getTotals(campaign).pending}</TableCell>
                      <TableCell align="right">
                        <Link
                          component="button"
                          variant="body2"
                          color="inherit"
                          underline="always"
                          onClick={() => setDetails({ campaign, filter: "sent" })}
                        >
                          {getTotals(campaign).sent}
                        </Link>
                      </TableCell>
                      <TableCell align="right">
                        <Link
                          component="button"
                          variant="body2"
                          color="inherit"
                          underline="always"
                          onClick={() => setDetails({ campaign, filter: "failed" })}
                        >
                          {getTotals(campaign).failed}
                        </Link>
                      </TableCell>
                      <TableCell align="right">
                        <Link
                          component="button"
                          variant="body2"
                          color="inherit"
                          underline="always"
                          onClick={() => setDetails({ campaign, filter: "skipped" })}
                        >
                          {getTotals(campaign).skipped}
                        </Link>
                      </TableCell>
                      <TableCell className={classes.nowrap}>
                        {campaign.createdAt ? datetimeToClient(campaign.createdAt) : "-"}
                      </TableCell>
                      <TableCell align="right" className={classes.nowrap}>
                        <Tooltip title={i18n.t("invoiceCampaigns.details.open")}>
                          <IconButton
                            size="small"
                            aria-label={i18n.t("invoiceCampaigns.details.open")}
                            onClick={() => setDetails({ campaign, filter: "all" })}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isInProgress(campaign) && (
                          <Tooltip title={i18n.t("invoiceCampaigns.buttons.cancel")}>
                            <IconButton
                              size="small"
                              aria-label={i18n.t("invoiceCampaigns.buttons.cancel")}
                              style={{ color: SEMANTIC.error }}
                              onClick={() => {
                                setCancelling(campaign);
                                setConfirmCancelOpen(true);
                              }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && campaigns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} style={{ borderBottom: "none" }}>
                        <EmptyState
                          icon={<ReceiptIcon />}
                          title={i18n.t("invoiceCampaigns.table.empty")}
                          message="Preencha o formulário acima para criar a primeira."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                  {loading && <TableRowSkeleton columns={9} />}
                </>
              </TableBody>
            </Table>
          </div>
        </Section>
      </div>
    </MainContainer>
  );
};

export default InvoiceCampaigns;
